import type { CityConfig } from "../city-template/types";

export const bangorConfig: CityConfig = {
  city: "Bangor",
  state: "Maine",
  stateAbbr: "ME",
  slug: "bangor",
  route: "/cities/bangor",
  title: "Emergency & Fast Passport Renewal Bangor ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Bangor, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Bangor, Brewer, Old Town, and Orono travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bangor, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Bangor and Penobscot County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bangor%20Maine%20downtown%20with%20Penobscot%20River%20waterfront%2C%20historic%20buildings%20along%20Main%20Street%2C%20Thomas%20Hill%20Standpipe%20visible%2C%20lush%20green%20trees%20and%20New%20England%20charm%2C%20warm%20afternoon%20light%2C%20professional%20cityscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20and%20scenic%20small%20city%20scene&width=1800&height=700&seq=bangor-hero-001&orientation=landscape",
  heroImageAlt: "Bangor Maine downtown with Penobscot River waterfront and historic Main Street",
  geoPosition: "44.8016;-68.7712",
  geoPlacename: "Bangor, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 240 miles (4 hours) from Bangor, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Bangor", zips: "04401" },
    { name: "Broadway", zips: "04401" },
    { name: "Tree Streets", zips: "04401" },
    { name: "Brewer", zips: "04412" },
    { name: "Old Town", zips: "04468" },
    { name: "Orono", zips: "04469" },
    { name: "Hampden", zips: "04444" },
    { name: "Veazie", zips: "04401" },
    { name: "Milford", zips: "04461" },
    { name: "Holden", zips: "04429" },
    { name: "Glenburn", zips: "04401" },
    { name: "Kenduskeag", zips: "04450" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Bangor departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/cities/portland-me" },
    { name: "Emergency passport help in Lewiston", route: "/cities/lewiston" },
    { name: "Urgent passport services in South Portland", route: "/cities/south-portland" },
    { name: "Same-day passport help in Auburn", route: "/cities/auburn-me" },
    { name: "Expedited passport services in Augusta", route: "/cities/augusta-me" },
    { name: "Passport courier services in Old Town", route: "/cities/old-town" },
    { name: "Rush passport help in Orono", route: "/cities/orono" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Bangor, Maine remotely via secure courier. We do not have a walk-in office in Bangor — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Bangor travelers.",
  fpcOfficeDistance: "Serving all of Penobscot County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire Bangor, Maine metro area. From downtown Bangor to the Tree Streets, from Broadway to the surrounding suburbs — we help Bangor travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Bangor neighborhoods and nearby cities including Brewer, Old Town, Orono, Hampden, Veazie, and Holden. If you are in Penobscot County or nearby Piscataquis County, we have you covered.",
  howItWorksIntro: "Most Bangor travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "With BGR (Bangor International Airport) serving northern and eastern Maine, Bangor travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Bangor travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Bangor? Here are the most common ones we hear from Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Bangor international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert H.",
      location: "Brewer, ME",
      rating: 5,
      text: "My flight to Dublin was in 3 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. I shipped from the Bangor UPS Store and had my passport the next day.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa P.",
      location: "Old Town, ME",
      rating: 5,
      text: "I needed to renew my passport for a cruise to the Caribbean. They reviewed my documents, caught an error on my form, and I had my passport in 5 days. Great service for northern Maine.",
      service: "Passport Renewal",
    },
    {
      name: "Thomas W.",
      location: "Orono, ME",
      rating: 5,
      text: "Lost my passport a week before a conference in Germany. Fast Passport Center handled the DS-64 process and got me a replacement in 2 days. Saved my academic career.",
      service: "Lost Passport",
    },
    {
      name: "Karen D.",
      location: "Hampden, ME",
      rating: 5,
      text: "As a frequent traveler from Bangor, I need a reliable passport service. Fast Passport Center has been my go-to for years. Professional, fast, and always dependable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "bangor-1",
      name: "Bangor Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "73 Harlow St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 945-4613",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Bangor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "bangor-2",
      name: "Brewer Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "310 State St",
      city: "Brewer",
      state: "ME",
      zip: "04412",
      phone: "(207) 989-4435",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2 miles from downtown Bangor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Brewer residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "bangor-3",
      name: "Old Town Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "141 Center St",
      city: "Old Town",
      state: "ME",
      zip: "04468",
      phone: "(207) 827-3203",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from downtown Bangor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Old Town and Orono residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "bangor-4",
      name: "Orono Town Office",
      type: "Town Office Passport Acceptance",
      address: "59 Main St",
      city: "Orono",
      state: "ME",
      zip: "04469",
      phone: "(207) 866-2556",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from downtown Bangor",
      website: "https://www.orono.org/",
      websiteLabel: "Orono Town Office",
      notes: "Accepts walk-ins but appointments preferred. Photo services not available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "bangor-5",
      name: "Hampden Town Office",
      type: "Town Office Passport Acceptance",
      address: "106 Western Ave",
      city: "Hampden",
      state: "ME",
      zip: "04444",
      phone: "(207) 862-3034",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "6 miles from downtown Bangor",
      website: "https://www.hampdenmaine.gov/",
      websiteLabel: "Hampden Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
    {
      id: "bangor-6",
      name: "Holden Town Office",
      type: "Town Office Passport Acceptance",
      address: "137 Main Rd",
      city: "Holden",
      state: "ME",
      zip: "04429",
      phone: "(207) 843-5542",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "12 miles from downtown Bangor",
      website: "https://www.holdenmaine.com/",
      websiteLabel: "Holden Town Office",
      notes: "Popular for Holden and surrounding towns. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Bangor acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Bangor applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Bangor families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Bangor travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Bangor." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Bangor address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Bangor, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Bangor in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Bangor travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Bangor, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Bangor residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Bangor, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Bangor with no office visit required. We serve all Bangor neighborhoods including downtown, Broadway, the Tree Streets, and the surrounding areas. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Bangor, Maine?",
      a: "The fastest way to get an urgent passport in Bangor is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in Bangor, Maine?",
      a: "With Fast Passport Center's emergency courier service, Bangor travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Bangor?",
      a: "Popular options include the Bangor Main Post Office (04401), Brewer Post Office (04412), Old Town Post Office (04468), and Orono Town Office (04469). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Brewer, Old Town, or Orono?",
      a: "We serve all of Penobscot County including Brewer, Old Town, Orono, Hampden, Veazie, and Holden. Our overnight shipping labels work from any UPS location, so your location within the Bangor metro doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Bangor?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Bangor?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Bangor offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Bangor?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Bangor?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Bangor families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bangor", address: "46 Bangor Mall Blvd, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Bangor", address: "617 Broadway, Bangor, ME 04401", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — Bangor", address: "263 State St, Bangor, ME 04401", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Bangor", address: "900 Stillwater Ave, Bangor, ME 04401", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Brewer", address: "387 Wilson St, Brewer, ME 04412", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Old Town", address: "139 Center St, Old Town, ME 04468", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Orono", address: "6 Mill St, Orono, ME 04469", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Hampden", address: "44 Main Rd N, Hampden, ME 04444", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Bangor",
      address: "1129 Union St, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Orono",
      address: "63 Main St, Orono, ME 04469",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "BMV — Old Town",
      address: "89 Main St, Old Town, ME 04468",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "BMV — Hampden",
      address: "106 Western Ave, Hampden, ME 04444",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "3 miles from downtown Bangor",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport with seasonal international flights to Canada and Ireland. Direct flights to major U.S. hubs including Florida and NYC.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "130 miles from Bangor (2.5 hours)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with more direct flight options. Many Bangor travelers drive to Portland for international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "240 miles from Bangor (4 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many Bangor travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};